import * as Yup from 'yup'
import { ChainProtocol, OperationType, PayoutToken } from '@archax/shared-types'
import { Button, ButtonGroup, Grid } from '@mui/material'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation, useQuery } from '@tanstack/react-query'
import Dialog from '../../../components/Dialog/Dialog'
import onApiError from '../../../util/on-api-error'
import { createOperationForPayoutContract } from '../../../api/operations'
import { getAvailablePayoutTokensByProtocol } from '../../../api/payouts'
import { Select } from '../../../components/ui/Select'

type SetPayoutTokenProps = {
  payoutContractId: string
  payoutTokenAddress: string
  protocol: ChainProtocol
  onClose: () => void
  onConfirm: () => void
}
export default function SetPayoutToken({
  payoutContractId,
  payoutTokenAddress,
  protocol,
  onClose,
}: SetPayoutTokenProps) {
  const { t } = useTranslation()

  const { data: availablePayoutTokensData, refetch: refetchAvailablePayoutTokens } = useQuery(
    [`get-available-payout-tokens-${protocol}`],
    () =>
      (async () => {
        const response = await getAvailablePayoutTokensByProtocol(protocol)
        return response.data
      })(),
    {
      retry: false,
      onError: onApiError,
      cacheTime: 0,
    },
  )

  const { mutate: setPayoutTokenMutation } = useMutation(
    (data: { payoutToken: PayoutToken }) =>
      createOperationForPayoutContract(
        OperationType.PayoutContractSetPayoutToken,
        { payoutToken: data.payoutToken },
        { payoutContractId },
      ),
    {
      onSuccess: () => {
        toast.success(t('payoutContracts.showDetails.actions.setPayoutToken.successMessage'))
      },
      onError: onApiError,
    },
  )

  const validationSchema = Yup.object()
    .shape({
      payoutToken: Yup.object().required(),
    })
    .required()

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting, errors },
  } = useForm<{ payoutToken: PayoutToken }>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {},
    resolver: yupResolver(validationSchema),
  })

  const onSubmit = (data: { payoutToken: PayoutToken }) => {
    setPayoutTokenMutation(data)
    onClose()
  }

  return (
    <Dialog title={t('payoutContracts.showDetails.actions.setPayoutToken.message')} open={true} onClose={onClose}>
      <Grid minWidth={540}>
        <form data-testid="set-payout-token" onSubmit={handleSubmit(onSubmit)}>
          <Select
            fullWidth
            data-testid="set-payout-token__input"
            id="payoutToken"
            name="payoutToken"
            label={t('payoutContracts.showDetails.actions.setPayoutToken.input')}
            control={control}
            options={(availablePayoutTokensData || [])
              .filter((it: PayoutToken) => it.address !== payoutTokenAddress)
              .map((it: PayoutToken) => {
                return {
                  value: it,
                  label: it.name,
                }
              })}
          />

          <ButtonGroup fullWidth>
            <Button
              data-testid="set-payout-token__submit-button"
              disabled={!isValid || isSubmitting}
              type="submit"
              variant="contained"
              size="large"
              color="primary"
              fullWidth
            >
              {t('payoutContracts.showDetails.actions.setPayoutToken.button')}
            </Button>
          </ButtonGroup>
        </form>
      </Grid>
    </Dialog>
  )
}
